import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  FiSend, 
  FiPaperclip, 
  FiX, 
  FiUsers, 
  FiMessageCircle,
  FiArrowLeft,
  FiSearch,
  FiMessageSquare,
  FiPlus,
  FiUser,
  FiRefreshCw,
  FiFile,
  FiUserPlus,
  FiHash,
  FiBriefcase,
  FiFlag,
  FiGlobe,
  FiHome,
  FiChevronDown,
  FiCheck,
  FiUserCheck,
  FiSettings,
  FiLock,
  FiAlertCircle,
  FiClock,
  FiCalendar,
  FiLogOut,
  FiMapPin,
  FiMap
} from 'react-icons/fi';
import api from '../../../config/api';
import { useWebSocket } from '../../../config/websocket';
import './Chat.css';

const Chat = () => {
  const [conversations, setConversations] = useState([]);
  const [groups, setGroups] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  const [showNewChat, setShowNewChat] = useState(false);
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [typingUsers, setTypingUsers] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  
  // Group states
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');
  const [newGroupType, setNewGroupType] = useState('');
  const [selectedUsersForGroup, setSelectedUsersForGroup] = useState([]);
  const [groupDetails, setGroupDetails] = useState(null);
  const [showGroupDetails, setShowGroupDetails] = useState(false);
  const [activeTab, setActiveTab] = useState('chats');
  const [userRole, setUserRole] = useState('');
  const [alert, setAlert] = useState({ show: false, message: '', type: 'success' });

  // Confirmation modal state
  const [confirmState, setConfirmState] = useState({
    show: false,
    message: '',
    onConfirm: null,
    onCancel: null,
  });

  // NEW: Floating Action Button State
  const [fabOpen, setFabOpen] = useState(false);

  const activeConversationRef = useRef(activeConversation);
  const messagesRef = useRef(messages);
  const conversationsRef = useRef(conversations);
  const groupsRef = useRef(groups);
  const fabRef = useRef(null);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const typingDebounceRef = useRef(null);

  const currentUserId = localStorage.getItem('current_user_id');
  const jwtToken = localStorage.getItem('jwt_token');

  // FAB Actions Configuration
  const fabActions = [
    {
      id: 'new-chat',
      label: 'New Chat',
      icon: <FiMessageCircle />,
      action: () => {
        setFabOpen(false);
        openNewChat();
      }
    },
    {
      id: 'new-group',
      label: 'New Group',
      icon: <FiUserPlus />,
      action: () => {
        setFabOpen(false);
        if (availableGroupTypes.length === 0) {
          showAlert('You do not have permission to create groups', 'error');
          return;
        }
        setShowNewGroup(true);
      }
    },
    {
      id: 'contact-admin',
      label: 'Contact Admin',
      icon: <FiUsers />,
      action: () => {
        setFabOpen(false);
        findAdminHelper();
      }
    },
    {
      id: 'refresh',
      label: 'Refresh',
      icon: <FiRefreshCw />,
      action: () => {
        setFabOpen(false);
        refreshChat();
      }
    }
  ];

  // Custom confirm function
  const confirm = useCallback((message, onConfirm, onCancel = () => {}) => {
    setConfirmState({
      show: true,
      message,
      onConfirm: () => {
        setConfirmState({ show: false, message: '', onConfirm: null, onCancel: null });
        onConfirm();
      },
      onCancel: () => {
        setConfirmState({ show: false, message: '', onConfirm: null, onCancel: null });
        onCancel();
      },
    });
  }, []);

  // Confirmation Modal Component
  const ConfirmationModal = () => {
    if (!confirmState.show) return null;

    return (
      <div className="modal-overlay" onClick={confirmState.onCancel}>
        <div className="confirmation-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-content">
            <div className="confirmation-header">
              <FiAlertCircle className="warning-icon" />
              <h3>Confirmation</h3>
            </div>
            <p className="confirmation-message">{confirmState.message}</p>
            <div className="modal-actions">
              <button className="secondary-button" onClick={confirmState.onCancel}>
                Cancel
              </button>
              <button className="primary-button danger" onClick={confirmState.onConfirm}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Close FAB when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (fabRef.current && !fabRef.current.contains(event.target)) {
        setFabOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Show alert function
  const showAlert = useCallback((message, type = 'success') => {
    setAlert({ show: true, message, type });
    setTimeout(() => {
      setAlert({ show: false, message: '', type: 'success' });
    }, 5000);
  }, []);

  // Get user role from localStorage
  useEffect(() => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        console.log('User data from localStorage:', user);
        
        let role = '';
        
        if (user.roles && Array.isArray(user.roles) && user.roles.length > 0) {
          role = user.roles[0].toLowerCase().replace(/\s+/g, '_');
        } else if (user.role) {
          role = user.role.toLowerCase();
        } else if (user.adminRoleType) {
          role = user.adminRoleType.toLowerCase().replace(/\s+/g, '_');
        } else if (user.type) {
          role = user.type.toLowerCase().replace(/\s+/g, '_');
        } else if (user.role_type) {
          role = user.role_type.toLowerCase().replace(/\s+/g, '_');
        } else if (user.roleName) {
          role = user.roleName.toLowerCase().replace(/\s+/g, '_');
        } else {
          role = 'member';
        }
        
        setUserRole(role);
      } else {
        setUserRole('member');
      }
    } catch (error) {
      console.error('Error parsing user role:', error);
      setUserRole('member');
    }
  }, []);

  // Helper to get available group types based on USER ROLE
  const getAvailableGroupTypes = useCallback(() => {
    const allTypes = [
      { 
        value: 'polling_unit', 
        label: 'Polling Unit', 
        icon: <FiHome />,
        description: 'Smallest administrative unit - for polling station level communication',
        color: '#0578d4',
        level: 1,
        details: [
          'Smallest administrative unit',
          'For polling station level communication',
          'Ideal for local coordination'
        ]
      },
      { 
        value: 'ward', 
        label: 'Ward', 
        icon: <FiMapPin />,
        description: 'Ward level - for ward coordinators and officials',
        color: '#28a745',
        level: 2,
        details: [
          'Ward level communication',
          'For ward coordinators and officials',
          'Medium-scale coordination'
        ]
      },
      { 
        value: 'local_government', 
        label: 'Local Government', 
        icon: <FiBriefcase />,
        description: 'LGA/LCDA level - for local government administration',
        color: '#ffc107',
        level: 3,
        details: [
          'LGA/LCDA level',
          'For local government administration',
          'Large-scale local coordination'
        ]
      },
      { 
        value: 'state', 
        label: 'State', 
        icon: <FiMap />,
        description: 'State level - for state-wide coordination',
        color: '#dc3545',
        level: 4,
        details: [
          'State level coordination',
          'For state-wide communication',
          'Regional administration'
        ]
      },
      { 
        value: 'nationwide', 
        label: 'Nationwide', 
        icon: <FiGlobe />,
        description: 'National level - for country-wide communication',
        color: '#6f42c1',
        level: 5,
        details: [
          'National level',
          'For country-wide communication',
          'Highest administrative level'
        ]
      }
    ];
    
    const roleLevels = {
      'super_admin': 5,
      'super-admin': 5,
      'superadmin': 5,
      'super': 5,
      'national': 5,
      'nationwide': 5,
      'state_admin': 4,
      'state-admin': 4,
      'stateadmin': 4,
      'state': 4,
      'local_government_admin': 3,
      'local_government': 3,
      'lga_admin': 3,
      'lga': 3,
      'lg-admin': 3,
      'local': 3,
      'ward_admin': 2,
      'ward-admin': 2,
      'wardadmin': 2,
      'ward': 2,
      'polling_unit_admin': 1,
      'polling_unit': 1,
      'polling-unit-admin': 1,
      'polling-unit': 1,
      'pu_admin': 1,
      'pu': 1,
      'polling': 1,
      'member': 0,
      'user': 0,
      'default': 0
    };
    
    const normalizedRole = userRole.trim().toLowerCase().replace(/\s+/g, '_');
    const userLevel = roleLevels[normalizedRole] !== undefined ? roleLevels[normalizedRole] : 0;
    
    const availableTypes = allTypes.filter(type => type.level <= userLevel);
    
    return availableTypes;
  }, [userRole]);

  const availableGroupTypes = getAvailableGroupTypes();

  // NEW: Leave group function
  const handleLeaveGroup = async (groupId) => {
    confirm('Are you sure you want to leave this group?', async () => {
      try {
        const response = await api.post(`/api/v1/groups/${groupId}/leave`);
        
        if (response.data.success) {
          // Remove group from local state
          setGroups(prev => prev.filter(group => group.id !== groupId));
          
          // If this was the active conversation, go back to chat list
          if (activeConversation?.id === groupId) {
            setActiveConversation(null);
            setShowSidebar(true);
          }
          
          showAlert('Successfully left the group', 'success');
        } else {
          showAlert('Failed to leave group', 'error');
        }
      } catch (error) {
        console.error('Error leaving group:', error);
        showAlert('Error leaving group. Please try again.', 'error');
      }
    });
  };

  // NEW: Remove member from group (admin only)
  const handleRemoveMember = async (groupId, userId) => {
    confirm('Are you sure you want to remove this member from the group?', async () => {
      try {
        const response = await api.delete(`/api/v1/groups/${groupId}/members/${userId}`);
        
        if (response.data.success) {
          // Update group details
          if (groupDetails?.id === groupId) {
            setGroupDetails(prev => ({
              ...prev,
              members: prev.members.filter(member => member.id !== userId),
              members_count: prev.members_count - 1
            }));
          }
          
          showAlert('Member removed successfully', 'success');
        } else {
          showAlert('Failed to remove member', 'error');
        }
      } catch (error) {
        console.error('Error removing member:', error);
        showAlert('Error removing member. Please try again.', 'error');
      }
    });
  };

  // Typing indicator function
  const sendTypingIndicator = useCallback(async (id, isTyping, isGroup = false) => {
    if (!id || id.startsWith('temp-')) {
      return;
    }
    
    try {
      if (typingDebounceRef.current) {
        clearTimeout(typingDebounceRef.current);
      }
      
      typingDebounceRef.current = setTimeout(async () => {
        const payload = {
          is_typing: isTyping
        };

        if (isGroup) {
          payload.chat_group_id = id;
        } else {
          payload.conversation_id = id;
        }

        try {
          await api.post('/chat/typing', payload);
        } catch (error) {
          // Silent error handling
        }
      }, 300);
      
    } catch (error) {
      // Silent error handling
    }
  }, []);

  const triggerBottomNavUpdate = useCallback(() => {
    const currentConversations = conversationsRef.current;
    const currentGroups = groupsRef.current;
    
    if ((!currentConversations || currentConversations.length === 0) && 
        (!currentGroups || currentGroups.length === 0)) return;

    try {
      const totalUnread = [...(currentConversations || []), ...(currentGroups || [])].reduce((total, conv) => {
        return total + (conv.unread_count || 0);
      }, 0);
      
      const currentUnread = localStorage.getItem('global_unread_count');
      if (currentUnread !== totalUnread.toString()) {
        localStorage.setItem('global_unread_count', totalUnread.toString());
      }
    } catch (error) {
      // Silent error handling
    }
    
    if (window.bottomNavUpdateTimeout) {
      clearTimeout(window.bottomNavUpdateTimeout);
    }
    
    window.bottomNavUpdateTimeout = setTimeout(() => {
      window.dispatchEvent(new Event('chatUpdated'));
      window.dispatchEvent(new Event('conversationsUpdated'));
      window.dispatchEvent(new CustomEvent('unreadCountUpdated', { 
        detail: { count: [...(conversationsRef.current || []), ...(groupsRef.current || [])].reduce((total, conv) => total + (conv.unread_count || 0), 0) }
      }));
    }, 500);
  }, []);

  // Update refs without triggering re-renders
  useEffect(() => {
    activeConversationRef.current = activeConversation;
  }, [activeConversation]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  useEffect(() => {
    groupsRef.current = groups;
  }, [groups]);

  // Trigger updates on meaningful changes
  useEffect(() => {
    if (conversations.length > 0 || groups.length > 0) {
      const totalUnread = [...conversations, ...groups].reduce((total, conv) => total + (conv.unread_count || 0), 0);
      const currentUnread = localStorage.getItem('global_unread_count');
      
      if (currentUnread !== totalUnread.toString()) {
        triggerBottomNavUpdate();
      }
    }
  }, [conversations, groups, triggerBottomNavUpdate]);

  const {
    initialize,
    subscribeToUserChannel,
    subscribeToConversation,
    subscribeToGroupChannel,
    joinPresenceChannel,
    leaveConversation,
    disconnect,
    isConnected: websocketConnected
  } = useWebSocket(jwtToken, currentUserId);

  const markMessagesAsRead = useCallback(async (messageIds) => {
    try {
      if (!messageIds || messageIds.length === 0) return;

      const validMessageIds = messageIds.filter(id => id != null && id !== '');
      if (validMessageIds.length === 0) return;

      try {
        await api.post('/chat/mark-as-read', {
          message_ids: validMessageIds
        });
      } catch (error) {
        // Silent error handling
      }

      setMessages(prev => prev.map(msg => 
        validMessageIds.includes(msg.id) ? { ...msg, is_read: true } : msg
      ));
      
      if (activeConversation?.id) {
        if (activeConversation.type === 'group') {
          setGroups(prev => prev.map(group => 
            group.id === activeConversation.id ? { ...group, unread_count: 0 } : group
          ));
        } else {
          setConversations(prev => prev.map(conv => 
            conv.id === activeConversation.id ? { ...conv, unread_count: 0 } : conv
          ));
        }
      }
      
    } catch (error) {
      // Silent error handling
    }
  }, [activeConversation?.id, activeConversation?.type]);

  const handleGlobalMessageNotification = useCallback((event) => {
    if (!event) return;

    let message = null;
    let groupInfo = null;
    
    if (event.message) {
      message = event.message;
      groupInfo = event.group;
    } else if (event.data) {
      message = event.data;
    } else if (event.type === 'GroupCreated' && event.group) {
      handleGroupCreatedNotification(event.group);
      return;
    } else if (event.type === 'MemberAdded' && event.group_id) {
      handleMemberAddedNotification(event.group_id, event.user);
      return;
    } else {
      return;
    }

    if (message.chat_group_id || message.group || message.chat_group || groupInfo) {
      handleGroupMessageNotification(message, groupInfo);
      return;
    }

    const currentUserIdStr = currentUserId?.toString();
    const currentActiveConversation = activeConversationRef.current;

    setConversations(prev => {
      let conversationExists = null;
      
      if (message.conversation_id) {
        conversationExists = prev.find(conv => conv.id === message.conversation_id);
      }
      
      if (!conversationExists) {
        const otherUserId = message.sender_id.toString() !== currentUserIdStr 
          ? message.sender_id 
          : message.recipient_id;
        
        conversationExists = prev.find(conv => {
          if (conv.other_user?.id == otherUserId) return true;
          if (conv.participants && Array.isArray(conv.participants)) {
            return conv.participants.some(p => p.id == otherUserId);
          }
          return false;
        });
      }

      if (!conversationExists) {
        const isCurrentUserSender = message.sender_id.toString() === currentUserIdStr;
        const otherUserId = isCurrentUserSender ? message.recipient_id : message.sender_id;
        
        const otherUserData = {
          id: otherUserId,
          name: isCurrentUserSender 
            ? (message.recipient_name || message.recipient_full_name || 'User')
            : (message.sender_name || message.sender_full_name || 'User'),
          email: isCurrentUserSender ? message.recipient_email : message.sender_email,
          avatar: isCurrentUserSender ? message.recipient_avatar : message.sender_avatar,
        };

        const newConversation = {
          id: message.conversation_id,
          other_user: otherUserData,
          last_message: message,
          unread_count: message.sender_id.toString() !== currentUserIdStr ? 1 : 0,
          created_at: message.created_at,
          updated_at: message.created_at
        };
        
        return [newConversation, ...prev];
      }

      const updated = prev.map(conv => {
        const shouldUpdate = 
          conv.id === message.conversation_id || 
          (conv.other_user?.id == (message.sender_id.toString() !== currentUserIdStr ? message.sender_id : message.recipient_id));
        
        if (shouldUpdate) {
          const updatedId = message.conversation_id || conv.id;
          
          let updatedOtherUser = { ...conv.other_user };
          if (!updatedOtherUser.name || updatedOtherUser.name === 'User') {
            const isCurrentUserSender = message.sender_id.toString() === currentUserIdStr;
            updatedOtherUser = {
              ...updatedOtherUser,
              name: isCurrentUserSender 
                ? (message.recipient_name || message.recipient_full_name || updatedOtherUser.name)
                : (message.sender_name || message.sender_full_name || updatedOtherUser.name),
            };
          }
          
          const unreadIncrement = message.sender_id.toString() !== currentUserIdStr && 
                         (!currentActiveConversation || currentActiveConversation.id !== conv.id) ? 1 : 0;
          
          return {
            ...conv,
            id: updatedId,
            other_user: updatedOtherUser,
            last_message: message,
            unread_count: (conv.unread_count || 0) + unreadIncrement,
            updated_at: message.created_at
          };
        }
        return conv;
      });
      
      const updatedConv = updated.find(conv => 
        conv.id === message.conversation_id || 
        conv.other_user?.id == (message.sender_id.toString() !== currentUserIdStr ? message.sender_id : message.recipient_id)
      );
      
      let finalConversations;
      if (updatedConv) {
        finalConversations = [updatedConv, ...updated.filter(conv => 
          conv.id !== updatedConv.id && 
          (!conv.other_user || conv.other_user.id !== updatedConv.other_user?.id)
        )];
      } else {
        finalConversations = updated;
      }
      
      return finalConversations;
    });

    if (currentActiveConversation && 
        currentActiveConversation.type === 'direct' &&
        (currentActiveConversation.id === message.conversation_id ||
         currentActiveConversation.other_user?.id == (message.sender_id.toString() !== currentUserIdStr ? message.sender_id : message.recipient_id))) {
      
      setMessages(prev => {
        if (prev.some(msg => 
          msg.id === message.id || 
          (msg.reference_number && msg.reference_number === message.reference_number)
        )) {
          return prev;
        }
        return [...prev, message];
      });

      if (message.sender_id.toString() !== currentUserIdStr) {
        markMessagesAsRead([message.id]);
      }
      
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
    
  }, [currentUserId, markMessagesAsRead]);

  const handleGroupMessageNotification = useCallback((message, groupInfo) => {
    const currentUserIdStr = currentUserId?.toString();
    const currentActiveConversation = activeConversationRef.current;

    setGroups(prev => {
      const groupExists = prev.find(group => group.id === message.chat_group_id);
      
      if (!groupExists) {
        const newGroup = {
          id: message.chat_group_id,
          name: groupInfo?.name || 'New Group',
          description: groupInfo?.description || '',
          group_type: groupInfo?.group_type || 'ward',
          type: 'group',
          last_message: message,
          unread_count: message.sender_id.toString() !== currentUserIdStr ? 1 : 0,
          created_at: message.created_at,
          updated_at: message.created_at,
          participants_count: groupInfo?.participants_count || 0,
          members_count: groupInfo?.members_count || 0,
          is_member: true
        };
        
        return [newGroup, ...prev];
      }

      const updated = prev.map(group => {
        if (group.id === message.chat_group_id) {
          const unreadIncrement = message.sender_id.toString() !== currentUserIdStr && 
                         (!currentActiveConversation || currentActiveConversation.id !== group.id) ? 1 : 0;
          
          return {
            ...group,
            last_message: message,
            unread_count: (group.unread_count || 0) + unreadIncrement,
            updated_at: message.created_at
          };
        }
        return group;
      });
      
      const updatedGroup = updated.find(g => g.id === message.chat_group_id);
      const otherGroups = updated.filter(g => g.id !== message.chat_group_id);
      return [updatedGroup, ...otherGroups];
    });

    if (currentActiveConversation && 
        currentActiveConversation.type === 'group' &&
        currentActiveConversation.id === message.chat_group_id) {
      
      setMessages(prev => {
        if (prev.some(msg => 
          msg.id === message.id || 
          (msg.reference_number && msg.reference_number === message.reference_number)
        )) {
          return prev;
        }
        return [...prev, message];
      });

      if (message.sender_id.toString() !== currentUserIdStr) {
        markMessagesAsRead([message.id]);
      }
      
      scrollToBottom();
    }
  }, [currentUserId, markMessagesAsRead]);

  const handleGroupCreatedNotification = useCallback((groupData) => {
    const isCreator = groupData.creator_id == currentUserId;
    
    if (!isCreator) {
      const newGroup = {
        ...groupData,
        type: 'group',
        unread_count: 0,
        is_member: true,
        user_role: 'member',
        members_count: groupData.members_count || groupData.participants_count || 1,
        updated_at: groupData.created_at || new Date().toISOString()
      };
      
      setGroups(prev => {
        const exists = prev.find(g => g.id === groupData.id);
        if (exists) {
          return prev;
        }
        return [newGroup, ...prev];
      });
      
      showAlert(`You've been added to group: ${groupData.name}`, 'info');
    }
  }, [currentUserId, showAlert]);

  const handleMemberAddedNotification = useCallback((groupId, userData) => {
    if (userData.id == currentUserId) {
      loadGroups();
      showAlert(`You've been added to a group`, 'info');
    }
    
    if (activeConversationRef.current?.id === groupId) {
      loadGroupDetails(groupId);
    }
  }, [currentUserId, showAlert]);

  const handleTypingIndicator = (typingEvent) => {
    if (typingEvent.is_typing) {
      setTypingUsers(prev => [...prev.filter(u => u.id !== typingEvent.user.id), typingEvent.user]);
      
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      typingTimeoutRef.current = setTimeout(() => {
        setTypingUsers(prev => prev.filter(u => u.id !== typingEvent.user.id));
      }, 3000);
    } else {
      setTypingUsers(prev => prev.filter(u => u.id !== typingEvent.user.id));
      
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    }
  };

  // WebSocket initialization
  useEffect(() => {
    if (!jwtToken || !currentUserId) {
      return;
    }

    const echo = initialize();
    
    if (echo) {
      setIsConnected(true);
      
      subscribeToUserChannel(
        (event) => {
          handleGlobalMessageNotification(event);
        },
        (groupData) => {
          handleGroupCreatedNotification(groupData);
        },
        (event) => {
          handleMemberAddedNotification(event.group_id, event.user);
        }
      );
    }

    return () => {
      disconnect();
    };
  }, [jwtToken, currentUserId, initialize, subscribeToUserChannel, disconnect, handleGlobalMessageNotification]);

  // Clean up WebSocket when conversation changes
  useEffect(() => {
    if (!activeConversation || (!activeConversation.id && !activeConversation.other_user?.id)) {
      return;
    }

    const channelIdentifier = activeConversation.id || `temp-${activeConversation.other_user.id}`;

    return () => {
      leaveConversation(channelIdentifier);
    };
  }, [activeConversation, leaveConversation]);

  // WebSocket subscription for conversations and groups
  useEffect(() => {
    if (activeConversation?.id) {
      const conversationId = activeConversation.id;
      const currentUserIdStr = currentUserId?.toString();
      const isGroup = activeConversation.type === 'group';

      if (!conversationId.startsWith('temp-')) {
        if (isGroup) {
          const groupChannel = subscribeToGroupChannel(
            conversationId,
            (message) => {
              if (message.sender_id.toString() !== currentUserIdStr) {
                setMessages(prev => {
                  if (prev.some(msg => msg.id === message.id || (msg.reference_number && msg.reference_number === message.reference_number))) {
                    return prev;
                  }
                  return [...prev, message];
                });
                
                if (message.id) {
                  markMessagesAsRead([message.id]);
                }
                
                scrollToBottom();
              } else {
                setMessages(prev => prev.map(msg => 
                  msg.reference_number === message.reference_number ? message : msg
                ));
              }
            },
            (typingEvent) => {
              if (typingEvent.user.id.toString() !== currentUserIdStr) {
                handleTypingIndicator(typingEvent);
              }
            }
          );

          return () => {
            if (groupChannel) {
              leaveConversation(conversationId);
            }
          };
        } else {
          const conversationChannel = subscribeToConversation(
            conversationId,
            (message) => {
              if (message.sender_id.toString() !== currentUserIdStr) {
                setMessages(prev => {
                  if (prev.some(msg => msg.id === message.id || (msg.reference_number && msg.reference_number === message.reference_number))) {
                    return prev;
                  }
                  return [...prev, message];
                });
                
                if (message.id) {
                  markMessagesAsRead([message.id]);
                }
                
                scrollToBottom();
              } else {
                setMessages(prev => prev.map(msg => 
                  msg.reference_number === message.reference_number ? message : msg
                ));
              }
            },
            (typingEvent) => {
              if (typingEvent.user.id.toString() !== currentUserIdStr) {
                handleTypingIndicator(typingEvent);
              }
            }
          );

          const presenceChannel = joinPresenceChannel(
            conversationId,
            (users) => {
              setOnlineUsers(users || []);
            },
            (user) => {
              setOnlineUsers(prev => {
                const filtered = prev.filter(u => u.id !== user.id);
                return [...filtered, user];
              });
            },
            (user) => {
              setOnlineUsers(prev => prev.filter(u => u.id !== user.id));
            }
          );

          return () => {
            if (conversationChannel || presenceChannel) {
              leaveConversation(conversationId);
            }
          };
        }
      }
    }
  }, [activeConversation?.id, activeConversation?.type, currentUserId, subscribeToConversation, subscribeToGroupChannel, joinPresenceChannel, leaveConversation, markMessagesAsRead]);

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
    }
  };

  const refreshChat = async () => {
    setRefreshing(true);
    try {
      await loadConversations();
      await loadGroups();
      if (activeConversation) {
        if (activeConversation.type === 'group') {
          await loadGroupMessages(activeConversation.id);
        } else {
          await loadMessages(activeConversation.other_user.id);
        }
      }
    } catch (error) {
      // Silent error handling
    } finally {
      setRefreshing(false);
    }
  };

  const loadConversations = async () => {
    try {
      setLoading(true);
      
      const response = await api.get('/chat/conversations');
      
      let conversationsData = [];
      
      if (response.data.success && response.data.data && Array.isArray(response.data.data.conversations)) {
        conversationsData = response.data.data.conversations;
      } else if (Array.isArray(response.data)) {
        conversationsData = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        conversationsData = response.data.data;
      }
      
      const processedConversations = conversationsData.map(conv => {
        if (!conv.other_user && conv.participants && Array.isArray(conv.participants)) {
          const otherParticipant = conv.participants.find(
            participant => participant.id && participant.id.toString() !== currentUserId?.toString()
          );
          if (otherParticipant) {
            return {
              ...conv,
              other_user: {
                id: otherParticipant.id,
                name: otherParticipant.full_name || 
                      (otherParticipant.first_name && otherParticipant.surname ? 
                       `${otherParticipant.first_name} ${otherParticipant.surname}` : 
                       otherParticipant.name) ||
                      otherParticipant.email ||
                      'User',
                email: otherParticipant.email,
                avatar: otherParticipant.passport_photo || otherParticipant.avatar || otherParticipant.profile_picture,
              }
            };
          }
        }
        return conv;
      });
      
      setConversations(processedConversations);
      
    } catch (error) {
      setConversations([]);
    } finally {
      setLoading(false);
    }
  };

  const loadGroups = async () => {
    try {
      setLoading(true);
      
      const response = await api.get('/groups');
      
      let groupsData = [];
      
      if (response.data.success && response.data.data && response.data.data.groups) {
        groupsData = response.data.data.groups;
      } else if (Array.isArray(response.data)) {
        groupsData = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        groupsData = response.data.data;
      }
      
      setGroups(groupsData);
      
    } catch (error) {
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async (search = '') => {
    try {
      setUsersLoading(true);
      
      const response = await api.get('/admins/members', {
        params: {
          search: search,
          per_page: 50
        }
      });
      
      let usersData = [];
      
      if (response.data.success && response.data.data && Array.isArray(response.data.data)) {
        usersData = response.data.data;
      } else if (Array.isArray(response.data)) {
        usersData = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        usersData = response.data.data;
      } else if (response.data && Array.isArray(response.data.users)) {
        usersData = response.data.users;
      } else if (response.data && Array.isArray(response.data.members)) {
        usersData = response.data.members;
      }
      
      const filteredUsers = usersData
        .filter(user => user && user.id && user.id.toString() !== currentUserId?.toString())
        .map(user => {
          let displayName = 'Unknown User';
          
          if (user.full_name && user.full_name.trim() !== '') {
            displayName = user.full_name;
          } else if (user.first_name && user.surname) {
            displayName = `${user.first_name} ${user.surname}`.trim();
          } else if (user.first_name) {
            displayName = user.first_name;
          } else if (user.surname) {
            displayName = user.surname;
          } else if (user.name && user.name.trim() !== '') {
            displayName = user.name;
          } else if (user.username && user.username.trim() !== '') {
            displayName = user.username;
          } else if (user.email) {
            displayName = user.email;
          }
          
          return {
            id: user.id,
            name: displayName,
            email: user.email || 'No email',
            avatar: user.passport_photo || user.avatar || user.profile_picture,
            is_online: user.is_online || false,
          };
        });
      
      setUsers(filteredUsers);
    } catch (error) {
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  const startNewConversation = async (user) => {
    try {
      setLoading(true);
      
      const response = await api.get(`/chat/conversation/${user.id}`);
      
      let conversationData = null;
      
      if (response.data.success && response.data.data) {
        conversationData = response.data.data;
      } else if (response.data) {
        conversationData = response.data;
      }
      
      if (conversationData) {
        const conversation = {
          id: conversationData.conversation_id || null,
          other_user: {
            id: user.id,
            name: user.name,
            email: user.email,
            avatar: user.avatar,
          },
          last_message: conversationData.messages?.[conversationData.messages.length - 1],
          unread_count: 0,
          messages: conversationData.messages || [],
          type: 'direct'
        };
        
        setConversations(prev => {
          const existsById = conversation.id && prev.find(conv => conv.id === conversation.id);
          const existsByUserId = prev.find(conv => conv.other_user?.id === user.id);
          
          let updatedConversations;
          if (existsById) {
            updatedConversations = prev.map(conv => conv.id === conversation.id ? conversation : conv);
          } else if (existsByUserId) {
            updatedConversations = prev.map(conv => conv.other_user?.id === user.id ? { ...conv, ...conversation } : conv);
          } else {
            updatedConversations = [conversation, ...prev];
          }
          
          return updatedConversations;
        });
        
        selectConversation(conversation);
        setShowNewChat(false);
        setUserSearchTerm('');
        setShowSidebar(false);
      } else {
        throw new Error('No conversation data received');
      }
      
    } catch (error) {
      showAlert('Failed to start conversation. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (userId) => {
    try {
      setLoading(true);
      
      const response = await api.get(`/chat/conversation/${userId}`);
      
      let messagesData = [];
      let conversationId = null;
      
      if (response.data.success && response.data.data) {
        messagesData = response.data.data.messages || [];
        conversationId = response.data.data.conversation_id;
      } else if (response.data && Array.isArray(response.data.messages)) {
        messagesData = response.data.messages;
        conversationId = response.data.conversation_id;
      } else if (response.data && Array.isArray(response.data.data)) {
        messagesData = response.data.data;
      } else if (Array.isArray(response.data)) {
        messagesData = response.data;
      }
      
      setMessages(messagesData.reverse());
      
      if (conversationId && activeConversation && activeConversation.other_user?.id) {
        if (activeConversation.id !== conversationId) {
          setActiveConversation(prev => ({
            ...prev,
            id: conversationId
          }));
          
          setConversations(prev => prev.map(conv => 
            conv.other_user?.id === userId ? { ...conv, id: conversationId } : conv
          ));
        }
      }
      
      const unreadMessages = messagesData.filter(msg => 
        !msg.is_read && msg.sender_id.toString() !== currentUserId
      );
      
      if (unreadMessages.length > 0) {
        const unreadMessageIds = unreadMessages.map(msg => msg.id).filter(id => id != null);
        if (unreadMessageIds.length > 0) {
          await markMessagesAsRead(unreadMessageIds);
        }
      }
      
    } catch (error) {
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const loadGroupMessages = async (groupId) => {
    try {
      setLoading(true);
      
      const response = await api.get(`/groups/${groupId}/messages`);
      
      let messagesData = [];
      
      if (response.data.success && response.data.data) {
        messagesData = response.data.data.messages || [];
      } else if (response.data && Array.isArray(response.data.messages)) {
        messagesData = response.data.messages;
      } else if (response.data && Array.isArray(response.data.data)) {
        messagesData = response.data.data;
      } else if (Array.isArray(response.data)) {
        messagesData = response.data;
      }
      
      setMessages(messagesData.reverse());
      
      const unreadMessages = messagesData.filter(msg => 
        !msg.is_read && msg.sender_id.toString() !== currentUserId
      );
      
      if (unreadMessages.length > 0) {
        const unreadMessageIds = unreadMessages.map(msg => msg.id).filter(id => id != null);
        if (unreadMessageIds.length > 0) {
          await markMessagesAsRead(unreadMessageIds);
        }
      }
      
    } catch (error) {
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);
    
    const conversationId = activeConversation?.id;
    const isGroup = activeConversation?.type === 'group';
    
    if (conversationId && !conversationId.startsWith('temp-')) {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      sendTypingIndicator(conversationId, true, isGroup);
      
      typingTimeoutRef.current = setTimeout(() => {
        sendTypingIndicator(conversationId, false, isGroup);
      }, 1000);
    }
    
    adjustTextareaHeight();
  };

  const selectConversation = (conversation) => {
    setActiveConversation({
      ...conversation,
      type: 'direct'
    });
    setShowSidebar(false);
    
    if (conversation.unread_count > 0) {
      setConversations(prev => prev.map(conv => 
        conv.id === conversation.id ? { ...conv, unread_count: 0 } : conv
      ));
    }
    
    if (conversation.other_user) {
      loadMessages(conversation.other_user.id);
    }
  };

  const selectGroup = (group) => {
    setActiveConversation({
      ...group,
      type: 'group'
    });
    setShowSidebar(false);
    
    if (group.unread_count > 0) {
      setGroups(prev => prev.map(g => 
        g.id === group.id ? { ...g, unread_count: 0 } : g
      ));
    }
    
    loadGroupMessages(group.id);
  };

  // Send message
  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() && attachments.length === 0) {
      return;
    }

    const referenceNumber = `msg-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    const isGroup = activeConversation?.type === 'group';

    try {
      const messageContent = newMessage.trim();

      // Create optimistic message
      const optimisticMessage = {
        id: null,
        [isGroup ? 'chat_group_id' : 'conversation_id']: activeConversation.id,
        sender_id: currentUserId,
        content: messageContent,
        created_at: new Date().toISOString(),
        is_read: false,
        reference_number: referenceNumber,
        sender: {
          id: currentUserId,
          first_name: localStorage.getItem('current_user_first_name') || 'You',
          surname: localStorage.getItem('current_user_surname') || '',
          passport_photo: localStorage.getItem('current_user_photo') || ''
        }
      };
      
      // Add optimistic message to UI immediately
      setMessages(prev => {
        if (prev.some(m => m.reference_number === referenceNumber)) {
          return prev;
        }
        return [...prev, optimisticMessage];
      });
      
      scrollToBottom();

      let response;
      
      if (isGroup) {
        if (attachments.length > 0) {
          const formData = new FormData();
          formData.append('message_type', 'file');
          formData.append('content', messageContent);
          formData.append('reference_number', referenceNumber);
          attachments.forEach(file => formData.append('attachments[]', file));

          response = await api.post(`/groups/${activeConversation.id}/messages`, formData, { 
            headers: { 'Content-Type': 'multipart/form-data' } 
          });
        } else {
          response = await api.post(`/groups/${activeConversation.id}/messages`, {
            message_type: 'text',
            content: messageContent,
            reference_number: referenceNumber,
          });
        }
      } else {
        if (!activeConversation || !activeConversation.other_user) {
          throw new Error('No active conversation selected');
        }

        const recipientId = activeConversation.other_user.id;
        
        if (attachments.length > 0) {
          const formData = new FormData();
          formData.append('recipient_id', recipientId);
          formData.append('message_type', 'file');
          formData.append('content', messageContent);
          formData.append('reference_number', referenceNumber);
          attachments.forEach(file => formData.append('attachments[]', file));

          response = await api.post('/chat/send', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        } else {
          response = await api.post('/chat/send', {
            recipient_id: recipientId,
            message_type: 'text',
            content: messageContent,
            reference_number: referenceNumber,
          });
        }
      }

      if (response.data.success && response.data.data && response.data.data.message) {
        const sentMessage = response.data.data.message;
        
        if (isGroup) {
          const resolvedGroupId = activeConversation.id;

          // Replace optimistic message with real message
          setMessages(prev => prev.map(msg => 
            msg.reference_number === referenceNumber 
              ? { ...sentMessage, reference_number: null }
              : msg
          ));
          
          setGroups(prev => {
            const updated = prev.map(group => {
              if (group.id === resolvedGroupId) {
                return {
                  ...group,
                  last_message: sentMessage,
                  updated_at: sentMessage.created_at
                };
              }
              return group;
            });
            
            const updatedGroup = updated.find(group => group.id === resolvedGroupId);
            let finalGroups;
            if (updatedGroup) {
              finalGroups = [updatedGroup, ...updated.filter(group => group.id !== resolvedGroupId)];
            } else {
              finalGroups = updated;
            }
            
            return finalGroups;
          });
        } else {
          const resolvedConversationId = response.data.data.conversation_id || activeConversation.id;
          const recipientId = activeConversation.other_user.id;

          // Replace optimistic message with real message
          setMessages(prev => prev.map(msg => 
            msg.reference_number === referenceNumber 
              ? { ...sentMessage, reference_number: null }
              : msg
          ));
          
          setConversations(prev => {
            const updated = prev.map(conv => {
              if (conv.other_user?.id === recipientId) {
                return {
                  ...conv,
                  id: resolvedConversationId,
                  last_message: sentMessage
                };
              }
              return conv;
            });
            
            const updatedConv = updated.find(conv => conv.id === resolvedConversationId);
            let finalConversations;
            if (updatedConv) {
              finalConversations = [updatedConv, ...updated.filter(conv => conv.id !== resolvedConversationId)];
            } else {
              finalConversations = updated;
            }
            
            return finalConversations;
          });

          if (activeConversation.id !== resolvedConversationId) {
            setActiveConversation(prev => ({ ...prev, id: resolvedConversationId }));
          }
        }

      } else {
        throw new Error('Invalid response data after sending message.');
      }

      setNewMessage('');
      setAttachments([]);
      adjustTextareaHeight();
      
      const conversationId = activeConversation.id;
      if (conversationId && !conversationId.startsWith('temp-')) {
        sendTypingIndicator(conversationId, false, isGroup);
      }
      
    } catch (error) {
      // Remove optimistic message on error
      setMessages(prev => prev.filter(msg => msg.reference_number !== referenceNumber));
      showAlert('Failed to send message. Please try again.', 'error');
      
      // If it's a backend error, show specific message
      if (error.response?.status === 500) {
        showAlert('Backend server error. Please contact support.', 'error');
      }
    }
  };

  const findAdminHelper = async () => {
    try {
      const response = await api.get('/chat/find-admin-helper');
      
      let adminHelper = null;
      
      if (response.data.success && response.data.data && response.data.data.admin_helper) {
        adminHelper = response.data.data.admin_helper;
      }
      
      if (adminHelper) {
        let adminName = 'Admin Helper';
        if (adminHelper.full_name && adminHelper.full_name.trim() !== '') {
          adminName = adminHelper.full_name;
        } else if (adminHelper.first_name && adminHelper.surname) {
          adminName = `${adminHelper.first_name} ${adminHelper.surname}`;
        }
        
        const adminUser = {
          id: adminHelper.id,
          name: adminName,
          email: adminHelper.email || 'No email',
          avatar: adminHelper.passport_photo,
          is_online: true
        };
        
        await startNewConversation(adminUser);
      } else {
        showAlert('No admin helper available at the moment.', 'info');
      }
    } catch (error) {
      showAlert('Failed to connect with admin helper.', 'error');
    }
  };

  const createNewGroup = async () => {
    try {
      if (!newGroupName.trim()) {
        showAlert('Please enter a group name', 'error');
        return;
      }

      if (!newGroupType) {
        showAlert('Please select a group type', 'error');
        return;
      }

      // Enhanced role level checking
      const roleLevels = {
        'super_admin': 5,
        'super-admin': 5,
        'superadmin': 5,
        'super': 5,
        'national': 5,
        'nationwide': 5,
        'state_admin': 4,
        'state-admin': 4,
        'stateadmin': 4,
        'state': 4,
        'local_government_admin': 3,
        'local_government': 3,
        'lga_admin': 3,
        'lga': 3,
        'lg-admin': 3,
        'local': 3,
        'ward_admin': 2,
        'ward-admin': 2,
        'wardadmin': 2,
        'ward': 2,
        'polling_unit_admin': 1,
        'polling_unit': 1,
        'polling-unit-admin': 1,
        'polling-unit': 1,
        'pu_admin': 1,
        'pu': 1,
        'polling': 1,
        'member': 0,
        'user': 0,
        'default': 0
      };

      const normalizedUserRole = userRole.trim().toLowerCase().replace(/\s+/g, '_');
      const userLevel = roleLevels[normalizedUserRole] !== undefined ? roleLevels[normalizedUserRole] : 0;

      const groupLevel = {
        'polling_unit': 1,
        'ward': 2,
        'local_government': 3,
        'state': 4,
        'nationwide': 5
      }[newGroupType] || 0;

      if (groupLevel > userLevel) {
        showAlert(`You don't have permission to create ${getGroupTypeLabel(newGroupType)} groups. Your role level: ${userLevel}, Required: ${groupLevel}`, 'error');
        return;
      }

      const groupData = {
        name: newGroupName.trim(),
        description: newGroupDescription.trim() || null,
        group_type: newGroupType,
        member_ids: selectedUsersForGroup.map(u => u.id),
        admin_ids: selectedUsersForGroup.filter(u => u.is_admin).map(u => u.id)
      };

      console.log('Creating group with data:', groupData);

      const response = await api.post('/groups', groupData);

      if (response.data.success) {
        const group = response.data.data.group || response.data.data;
        
        if (selectedUsersForGroup.length > 0) {
          const userIds = selectedUsersForGroup.map(u => u.id);
          const adminIds = selectedUsersForGroup.filter(u => u.is_admin).map(u => u.id);
          
          try {
            await api.post(`/groups/${group.id}/members`, {
              user_ids: userIds,
              admin_ids: adminIds,
              notify_users: true
            });
          } catch (addMemberError) {
            // Silent error handling
          }
        }
        
        const newGroup = {
          ...group,
          type: 'group',
          unread_count: 0,
          is_creator: true,
          user_role: 'admin',
          members_count: (group.members_count || 0) + selectedUsersForGroup.length + 1,
          updated_at: new Date().toISOString()
        };
        
        setGroups(prev => {
          const exists = prev.find(g => g.id === newGroup.id);
          if (exists) {
            return prev.map(g => g.id === newGroup.id ? newGroup : g);
          }
          return [newGroup, ...prev.filter(g => g.id !== newGroup.id)];
        });
        
        selectGroup(newGroup);
        setShowNewGroup(false);
        resetNewGroupForm();
        
        showAlert('Group created successfully! Members have been notified.', 'success');
        
        setTimeout(() => {
          loadGroups();
        }, 1000);
        
      } else {
        showAlert('Failed to create group', 'error');
      }
    } catch (error) {
      console.error('Error creating group:', error);
      if (error.response?.data?.message) {
        showAlert(`Error: ${error.response.data.message}`, 'error');
      } else if (error.response?.data?.errors) {
        const errorMessages = Object.values(error.response.data.errors).flat();
        showAlert(`Validation errors: ${errorMessages.join(', ')}`, 'error');
      } else {
        showAlert('Failed to create group. Please try again.', 'error');
      }
    }
  };

  const resetNewGroupForm = () => {
    setNewGroupName('');
    setNewGroupDescription('');
    setNewGroupType('');
    setSelectedUsersForGroup([]);
  };

  const handleCreateGroupSubmit = async (e) => {
    e.preventDefault();
    await createNewGroup();
  };

  const selectGroupType = (type) => {
    setNewGroupType(type);
  };

  const getGroupTypeIcon = (type) => {
    switch (type) {
      case 'polling_unit': return <FiHome />;
      case 'ward': return <FiMapPin />;
      case 'local_government': return <FiBriefcase />;
      case 'state': return <FiMap />;
      case 'nationwide': return <FiGlobe />;
      default: return <FiHash />;
    }
  };

  const getGroupTypeLabel = (type) => {
    switch (type) {
      case 'polling_unit': return 'Polling Unit';
      case 'ward': return 'Ward';
      case 'local_government': return 'Local Government';
      case 'state': return 'State';
      case 'nationwide': return 'Nationwide';
      default: return 'Select Group Type';
    }
  };

  const toggleUserForGroup = (user) => {
    setSelectedUsersForGroup(prev => {
      const exists = prev.find(u => u.id === user.id);
      if (exists) {
        return prev.filter(u => u.id !== user.id);
      } else {
        return [...prev, { ...user, is_admin: false }];
      }
    });
  };

  const toggleUserAdminStatus = (userId) => {
    setSelectedUsersForGroup(prev => 
      prev.map(user => {
        if (user.id === userId) {
          const newAdminStatus = !user.is_admin;
          return { ...user, is_admin: newAdminStatus };
        }
        return user;
      })
    );
  };

  const loadUsersForGroup = async (search = '') => {
    try {
      setUsersLoading(true);
      const response = await api.get('/admins/members', {
        params: { search, per_page: 100 }
      });
      
      let usersData = [];
      if (response.data.success && response.data.data && Array.isArray(response.data.data)) {
        usersData = response.data.data;
      }
      
      const filteredUsers = usersData
        .filter(user => user && user.id && user.id.toString() !== currentUserId?.toString())
        .map(user => {
          let displayName = 'Unknown User';
          
          if (user.full_name && user.full_name.trim() !== '') {
            displayName = user.full_name;
          } else if (user.first_name && user.surname) {
            displayName = `${user.first_name} ${user.surname}`.trim();
          } else if (user.first_name) {
            displayName = user.first_name;
          } else if (user.surname) {
            displayName = user.surname;
          } else if (user.name && user.name.trim() !== '') {
            displayName = user.name;
          } else if (user.username && user.username.trim() !== '') {
            displayName = user.username;
          } else if (user.email) {
            displayName = user.email;
          }
          
          return {
            id: user.id,
            name: displayName,
            email: user.email,
            avatar: user.passport_photo || user.avatar || '',
            is_online: user.is_online || false,
          };
        });
      
      setUsers(filteredUsers);
    } catch (error) {
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  const loadGroupDetails = async (groupId) => {
    try {
      const response = await api.get(`/groups/${groupId}`);
      if (response.data.success) {
        const groupData = response.data.data.group;
        setGroupDetails(groupData);
        setShowGroupDetails(true);
      }
    } catch (error) {
      showAlert('Failed to load group details', 'error');
    }
  };

  const handleFileAttach = (e) => {
    const files = Array.from(e.target.files);
    setAttachments(prev => [...prev, ...files]);
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const openNewChat = () => {
    setShowNewChat(true);
    setUserSearchTerm('');
    loadUsers();
  };

  const handleUserSearch = (e) => {
    const search = e.target.value;
    setUserSearchTerm(search);
    loadUsers(search);
  };

  const toggleSidebar = () => {
    setShowSidebar(!showSidebar);
  };

  const handleBack = () => {
    setActiveConversation(null);
    setShowSidebar(true);
  };

  const getOtherUser = (conversation) => {
    if (!conversation) return { name: 'Unknown User', id: null };
    
    if (conversation.other_user) {
      const otherUser = { ...conversation.other_user };
      
      if (!otherUser.name || otherUser.name === 'Unknown User' || otherUser.name === 'User') {
        if (otherUser.full_name && otherUser.full_name.trim() !== '') {
          otherUser.name = otherUser.full_name;
        } else if (otherUser.first_name && otherUser.surname) {
          otherUser.name = `${otherUser.first_name} ${otherUser.surname}`.trim();
        } else if (otherUser.first_name) {
          otherUser.name = otherUser.first_name;
        } else if (otherUser.surname) {
          otherUser.name = otherUser.surname;
        } else if (otherUser.email) {
          otherUser.name = otherUser.email.split('@')[0];
        } else if (otherUser.username) {
          otherUser.name = otherUser.username;
        } else {
          otherUser.name = 'User';
        }
      }
      
      return otherUser;
    }
    
    if (conversation.participants && Array.isArray(conversation.participants)) {
      const otherParticipant = conversation.participants.find(
        participant => participant.id && participant.id.toString() !== currentUserId?.toString()
      );
      
      if (otherParticipant) {
        let displayName = 'User';
        
        if (otherParticipant.full_name && otherParticipant.full_name.trim() !== '') {
          displayName = otherParticipant.full_name;
        } else if (otherParticipant.first_name && otherParticipant.surname) {
          displayName = `${otherParticipant.first_name} ${otherParticipant.surname}`.trim();
        } else if (otherParticipant.first_name) {
          displayName = otherParticipant.first_name;
        } else if (otherParticipant.surname) {
          displayName = otherParticipant.surname;
        } else if (otherParticipant.name && otherParticipant.name.trim() !== '') {
          displayName = otherParticipant.name;
        } else if (otherParticipant.email) {
          displayName = otherParticipant.email.split('@')[0];
        } else if (otherParticipant.username) {
          displayName = otherParticipant.username;
        }
        
        return {
          id: otherParticipant.id,
          name: displayName,
          avatar: otherParticipant.avatar || otherParticipant.passport_photo || otherParticipant.profile_picture,
          email: otherParticipant.email,
        };
      }
    }
    
    if (conversation.last_message) {
      const lastMessage = conversation.last_message;
      const isCurrentUserSender = lastMessage.sender_id.toString() === currentUserId?.toString();
      
      if (isCurrentUserSender && lastMessage.recipient_name) {
        return {
          id: lastMessage.recipient_id,
          name: lastMessage.recipient_name,
          email: lastMessage.recipient_email,
          avatar: lastMessage.recipient_avatar
        };
      } else if (!isCurrentUserSender && lastMessage.sender_name) {
        return {
          id: lastMessage.sender_id,
          name: lastMessage.sender_name,
          email: lastMessage.sender_email,
          avatar: lastMessage.sender_avatar
        };
      }
    }
    
    return { name: 'User', id: null };
  };

  const getLastMessage = (conversation) => {
    if (!conversation) return { content: 'No messages yet' };
    return conversation.last_message || { content: 'No messages yet' };
  };

  const isUserOnline = (userId) => {
    if (!userId) {
      return false;
    }
    
    const isOnline = onlineUsers.some(user => user && user.id && user.id.toString() === userId.toString());
    return isOnline;
  };

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
    } else {
      return date.toLocaleDateString([], {month: 'short', day: 'numeric'});
    }
  };

  const filteredConversations = Array.isArray(conversations) ? conversations.filter(conv => {
    if (!conv) return false;
    
    const otherUser = getOtherUser(conv);
    const lastMessage = getLastMessage(conv);
    
    const otherUserName = otherUser?.name || '';
    const lastMessageContent = lastMessage?.content || '';
    
    return (
      otherUserName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lastMessageContent.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }) : [];

  const filteredGroups = Array.isArray(groups) ? groups.filter(group => {
    if (!group) return false;
    
    const groupName = group.name || '';
    const lastMessageContent = group.last_message?.content || '';
    
    return (
      groupName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lastMessageContent.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }) : [];

  const filteredUsers = Array.isArray(users) ? users.filter(user => {
    if (!user) return false;
    
    const userName = user.name || '';
    const userEmail = user.email || '';
    
    return (
      userName.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
      userEmail.toLowerCase().includes(userSearchTerm.toLowerCase())
    );
  }) : [];

  // Initial load
  useEffect(() => {
    loadConversations();
    loadGroups();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    adjustTextareaHeight();
  }, [newMessage]);

  useEffect(() => {
    setIsConnected(websocketConnected);
  }, [websocketConnected]);

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (typingDebounceRef.current) {
        clearTimeout(typingDebounceRef.current);
      }
      if (window.bottomNavUpdateTimeout) {
        clearTimeout(window.bottomNavUpdateTimeout);
      }
    };
  }, []);

  // Auto-load users when new group modal opens
  useEffect(() => {
    if (showNewGroup) {
      loadUsersForGroup();
    }
  }, [showNewGroup]);

  // Alert Component
  const Alert = () => {
    if (!alert.show) return null;

    return (
      <div className={`alert-popup ${alert.type}`}>
        <div className="alert-content">
          <div className="alert-icon">
            {alert.type === 'success' ? '✅' : 
             alert.type === 'error' ? '❌' : 
             alert.type === 'info' ? 'ℹ️' : '⚠️'}
          </div>
          <div className="alert-message">{alert.message}</div>
          <button className="alert-close" onClick={() => setAlert({ show: false, message: '', type: 'success' })}>
            <FiX />
          </button>
        </div>
      </div>
    );
  };

  // Group Details Modal
  const GroupDetailsModal = () => {
    if (!showGroupDetails || !groupDetails) return null;

    const isAdmin = groupDetails.creator_id == currentUserId || 
                   (groupDetails.admins && groupDetails.admins.some(admin => admin.id == currentUserId));

    return (
      <div className="modal-overlay" onClick={() => setShowGroupDetails(false)}>
        <div className="group-details-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Group Details</h3>
            <button className="icon-button" onClick={() => setShowGroupDetails(false)}>
              <FiX />
            </button>
          </div>
          
          <div className="modal-content">
            <div className="group-header">
              <div className="group-avatar-large">
                {getGroupTypeIcon(groupDetails.group_type)}
              </div>
              <div className="group-info">
                <h2>{groupDetails.name}</h2>
                <p className="group-description">{groupDetails.description || 'No description'}</p>
                <div className="group-meta-details">
                  <span className="group-type-badge">{getGroupTypeLabel(groupDetails.group_type)}</span>
                  <span className="members-count">{groupDetails.members_count || 0} members</span>
                </div>
              </div>
            </div>

            <div className="group-actions">
              <button 
                className="leave-group-btn"
                onClick={() => {
                  setShowGroupDetails(false);
                  confirm('Are you sure you want to leave this group?', () => {
                    handleLeaveGroup(groupDetails.id);
                  });
                }}
              >
                <FiLogOut /> Leave Group
              </button>
            </div>

            <div className="members-section">
              <h4>Group Members ({groupDetails.members_count || 0})</h4>
              <div className="members-list">
                {groupDetails.members && groupDetails.members.map(member => (
                  <div key={member.id} className="member-item">
                    <div className="member-info">
                      <div className="avatar small">
                        {member.avatar ? (
                          <img src={member.avatar} alt={member.name} />
                        ) : (
                          <div className="avatar-placeholder">
                            {member.name?.charAt(0) || 'U'}
                          </div>
                        )}
                        <div className={`online-indicator ${member.is_online ? 'online' : 'offline'}`}></div>
                      </div>
                      <div className="member-details">
                        <span className="member-name">{member.name}</span>
                        <span className="member-email">{member.email}</span>
                      </div>
                    </div>
                    <div className="member-actions">
                      {member.id == groupDetails.creator_id && (
                        <span className="creator-badge">Creator</span>
                      )}
                      {groupDetails.admins && groupDetails.admins.some(admin => admin.id == member.id) && member.id != groupDetails.creator_id && (
                        <span className="admin-badge">Admin</span>
                      )}
                      {isAdmin && member.id != currentUserId && member.id != groupDetails.creator_id && (
                        <button 
                          className="remove-member-btn"
                          onClick={() => confirm(
                            `Are you sure you want to remove ${member.name} from the group?`,
                            () => handleRemoveMember(groupDetails.id, member.id)
                          )}
                          title="Remove member"
                        >
                          <FiX />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="chat-container">
      <Alert />
      <ConfirmationModal />
      
      {/* Left Panel - Chat List */}
      <div className={`chat-sidebar ${showSidebar || !activeConversation ? 'active' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-header-top">
            <h2>Messages</h2>
          </div>
          
          {/* Tabs for Chats/Groups */}
          <div className="chat-tabs">
            <button 
              className={`tab-button ${activeTab === 'chats' ? 'active' : ''}`}
              onClick={() => setActiveTab('chats')}
            >
              <FiMessageCircle /> Chats
            </button>
            <button 
              className={`tab-button ${activeTab === 'groups' ? 'active' : ''}`}
              onClick={() => setActiveTab('groups')}
            >
              <FiUsers /> Groups
            </button>
          </div>
          
          <div className="connection-status">
            <span>{isConnected ? '🟢 Connected' : '🟡 Connecting...'}</span>
            {refreshing && <span className="refreshing-text">Refreshing...</span>}
          </div>
        </div>

        {/* Search Box with FAB */}
        <div className="search-box-container">
          <div className="search-box">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder={`Search ${activeTab === 'groups' ? 'groups' : 'conversations'}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          {/* Floating Action Button */}
          <div className="fab-container-left" ref={fabRef}>
            <button 
              className={`fab-main-left ${fabOpen ? 'open' : ''}`}
              onClick={() => setFabOpen(!fabOpen)}
            >
              <FiPlus />
            </button>
            
            {fabOpen && (
              <div className="fab-menu-left">
                {fabActions.map((action, index) => (
                  <button
                    key={action.id}
                    className="fab-action-left"
                    onClick={action.action}
                    style={{ '--delay': index * 0.05 + 's' }}
                  >
                    <span className="fab-icon-left">{action.icon}</span>
                    <span className="fab-label-left">{action.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="conversations-list">
          {loading ? (
            <div className="loading">Loading...</div>
          ) : activeTab === 'chats' ? (
            filteredConversations.length === 0 ? (
              <div className="no-conversations">
                <FiMessageCircle size={48} />
                <p>No conversations yet</p>
              </div>
            ) : (
              filteredConversations.map(conversation => {
                const otherUser = getOtherUser(conversation);
                const lastMessage = getLastMessage(conversation);
                const isOnline = isUserOnline(otherUser.id);
                const lastMessageTime = lastMessage?.created_at ? formatMessageTime(lastMessage.created_at) : '';
                
                return (
                  <div
                    key={conversation.id || `temp-conv-${otherUser.id}`}
                    className={`conversation-item ${activeConversation?.id === conversation.id || (!activeConversation?.id && activeConversation?.other_user?.id === otherUser.id) ? 'active' : ''}`}
                    onClick={() => selectConversation(conversation)}
                  >
                    <div className="avatar">
                      {otherUser.avatar ? (
                        <img src={otherUser.avatar} alt={otherUser.name} />
                      ) : (
                        <div className="avatar-placeholder">
                          {otherUser.name?.charAt(0) || 'U'}
                        </div>
                      )}
                      <div className={`online-indicator ${isOnline ? 'online' : 'offline'}`}></div>
                    </div>
                    <div className="conversation-info">
                      <div className="conversation-header">
                        <h4>{otherUser.name || 'Unknown User'}</h4>
                        <div className="conversation-meta">
                          {lastMessageTime && (
                            <span className="conversation-time">{lastMessageTime}</span>
                          )}
                          {conversation.unread_count > 0 && (
                            <div className="unread-badge">
                              {conversation.unread_count > 99 ? '99+' : conversation.unread_count}
                            </div>
                          )}
                        </div>
                      </div>
                      <p className="last-message">
                        {lastMessage.content || 'No messages yet'}
                      </p>
                    </div>
                  </div>
                );
              })
            )
          ) : (
            filteredGroups.length === 0 ? (
              <div className="no-conversations">
                <FiUsers size={48} />
                <p>No groups yet</p>
              </div>
            ) : (
              filteredGroups.map(group => {
                const lastMessage = group.last_message || { content: 'No messages yet' };
                const lastMessageTime = lastMessage?.created_at ? formatMessageTime(lastMessage.created_at) : '';
                const senderName = lastMessage?.sender ? 
                  `${lastMessage.sender.first_name || ''} ${lastMessage.sender.surname || ''}`.trim() : '';
                
                return (
                  <div
                    key={group.id}
                    className={`conversation-item group-item ${activeConversation?.id === group.id ? 'active' : ''}`}
                    onClick={() => selectGroup(group)}
                  >
                    <div className="avatar">
                      <div className="avatar-placeholder group-avatar" style={{ 
                        backgroundColor: availableGroupTypes.find(t => t.value === group.group_type)?.color || '#0578d4' 
                      }}>
                        {getGroupTypeIcon(group.group_type)}
                      </div>
                    </div>
                    <div className="conversation-info">
                      <div className="conversation-header">
                        <h4>
                          {group.name}
                          <span className="participants-count"> ({group.members_count || group.participants_count || 0})</span>
                        </h4>
                        <div className="conversation-meta">
                          {lastMessageTime && (
                            <span className="conversation-time">{lastMessageTime}</span>
                          )}
                          {group.unread_count > 0 && (
                            <div className="unread-badge">
                              {group.unread_count > 99 ? '99+' : group.unread_count}
                            </div>
                          )}
                        </div>
                      </div>
                      <p className="last-message">
                        {senderName && <span className="sender-prefix">{senderName}: </span>}
                        {lastMessage.content || 'No messages yet'}
                      </p>
                      <div className="group-meta">
                        <span className="group-type">{getGroupTypeLabel(group.group_type)}</span>
                        {group.is_creator && <span className="creator-badge">Creator</span>}
                      </div>
                    </div>
                  </div>
                );
              })
            )
          )}
        </div>
      </div>

      {/* Right Panel - Active Chat */}
      <div className="chat-main">
        {activeConversation ? (
          <>
            <div className="chat-header">
              <button className="icon-button back-button" onClick={handleBack}>
                <FiArrowLeft />
              </button>
              <div className="chat-user-info">
                <div className="avatar">
                  {activeConversation.type === 'group' ? (
                    <div className="avatar-placeholder group-avatar" style={{ 
                      backgroundColor: availableGroupTypes.find(t => t.value === activeConversation.group_type)?.color || '#0578d4' 
                    }}>
                      {getGroupTypeIcon(activeConversation.group_type)}
                    </div>
                  ) : getOtherUser(activeConversation).avatar ? (
                    <img src={getOtherUser(activeConversation).avatar} alt={getOtherUser(activeConversation).name} />
                  ) : (
                    <div className="avatar-placeholder">
                      {getOtherUser(activeConversation).name?.charAt(0) || 'U'}
                    </div>
                  )}
                  {activeConversation.type !== 'group' && (
                    <div className={`online-indicator ${isUserOnline(getOtherUser(activeConversation).id) ? 'online' : 'offline'}`}></div>
                  )}
                </div>
                <div>
                  {/* <div className="chat-title">
                    <h3>
                      {activeConversation.type === 'group' 
                        ? activeConversation.name 
                        : getOtherUser(activeConversation).name || 'Unknown User'}
                    </h3>
                    {activeConversation.type === 'group' && (
                      <button 
                        className="icon-button small"
                        onClick={() => loadGroupDetails(activeConversation.id)}
                        title="Group details"
                      >
                        <FiSettings />
                      </button>
                    )}
                  </div> */}
                  <span className="user-status">
                    {activeConversation.type === 'group' 
                      ? `${activeConversation.members_count || activeConversation.participants_count || 0} members • ${getGroupTypeLabel(activeConversation.group_type)}`
                      : (isUserOnline(getOtherUser(activeConversation).id) ? '🟢 Online' : '🔴 Offline')}
                  </span>
                </div>
              </div>
            </div>

            <div className="messages-container">
              {loading ? (
                <div className="loading">Loading messages...</div>
              ) : messages.length === 0 ? (
                <div className="no-messages">
                  <FiMessageSquare size={64} />
                  <p>No messages yet</p>
                  <span>Start a conversation by sending a message</span>
                </div>
              ) : (
                <div className="messages-list">
                  {messages.map((message) => (
                    <div
                      key={message.id || `msg-${message.reference_number || Date.now()}`}
                      className={`message ${message.sender_id == currentUserId ? 'sent' : 'received'}`}
                    >
                      <div className="message-content">
                        {activeConversation.type === 'group' && message.sender_id != currentUserId && (
                          <div className="sender-name">
                            {message.sender?.first_name || message.sender?.name || 'User'} {message.sender?.surname || ''}
                          </div>
                        )}
                        <p>{message.content}</p>
                        {message.attachments && message.attachments.length > 0 && (
                          <div className="message-attachments">
                            {message.attachments.map((attachment, idx) => (
                              <div key={idx} className="attachment">
                                {attachment.type?.startsWith('image/') ? (
                                  <img src={attachment.url} alt="Attachment" />
                                ) : (
                                  <a href={attachment.url} target="_blank" rel="noopener noreferrer">
                                    <FiFile /> {attachment.name}
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        <span className="message-time">
                          {new Date(message.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                          {message.sender_id == currentUserId && (
                            <span className="read-status">
                              {message.is_read ? ' ✓✓' : ' ✓'}
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                  ))}
                  {typingUsers.map(user => (
                    <div key={user.id} className="typing-indicator">
                      {user.name} is typing...
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {attachments.length > 0 && (
              <div className="attachments-preview">
                {attachments.map((file, index) => (
                  <div key={index} className="attachment-preview">
                    {file.type.startsWith('image/') ? (
                      <img src={URL.createObjectURL(file)} alt="Preview" />
                    ) : (
                      <div className="file-preview">
                        <FiFile />
                        <span>{file.name}</span>
                      </div>
                    )}
                    <button onClick={() => removeAttachment(index)} className="remove-attachment">
                      <FiX />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <form onSubmit={sendMessage} className="message-input-form">
              <div className="message-input-container">
                <button
                  type="button"
                  className="icon-button"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <FiPaperclip />
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  multiple
                  onChange={handleFileAttach}
                />
                <textarea
                  ref={textareaRef}
                  className="message-input"
                  placeholder={`Type a message... ${activeConversation.type === 'group' ? '@ to mention someone' : ''}`}
                  value={newMessage}
                  onChange={handleInputChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage(e);
                    }
                  }}
                  rows={1}
                />
                <button 
                  type="submit" 
                  className="icon-button send-button" 
                  disabled={!newMessage.trim() && attachments.length === 0}
                >
                  <FiSend />
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="no-chat-selected">
            <FiMessageCircle size={80} />
            <h3>Select a conversation</h3>
            <p>Choose a conversation from the sidebar to start messaging</p>
          </div>
        )}
      </div>

      {/* New Chat Modal */}
      {showNewChat && (
        <div className="modal-overlay" onClick={() => setShowNewChat(false)}>
          <div className="new-chat-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>New Chat</h3>
              <button className="icon-button" onClick={() => setShowNewChat(false)}>
                <FiX />
              </button>
            </div>
            
            <div className="modal-search">
              <FiSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search users by name or email..."
                value={userSearchTerm}
                onChange={handleUserSearch}
                autoFocus
              />
            </div>
            
            <div className="users-list">
              {usersLoading ? (
                <div className="loading">Loading users...</div>
              ) : filteredUsers.length === 0 ? (
                <div className="no-users">
                  <FiUser size={48} />
                  <p>{userSearchTerm ? 'No users found' : 'No users available'}</p>
                  {userSearchTerm && (
                    <p className="search-tip">Try a different search term</p>
                  )}
                </div>
              ) : (
                <div className="users-list-content">
                  <div className="users-count">
                    {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''} found
                  </div>
                  {filteredUsers.map(user => (
                    <div
                      key={user.id}
                      className="user-item"
                      onClick={() => startNewConversation(user)}
                    >
                      <div className="avatar">
                        {user.avatar ? (
                          <img src={user.avatar} alt={user.name} />
                        ) : (
                          <div className="avatar-placeholder">
                            {user.name?.charAt(0) || 'U'}
                          </div>
                        )}
                        <div className={`online-indicator ${user.is_online ? 'online' : 'offline'}`}></div>
                      </div>
                      <div className="user-info">
                        <h4>{user.name}</h4>
                        <p className="user-email">{user.email}</p>
                      </div>
                      <div className="start-chat-indicator">
                        <FiMessageCircle />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* New Group Modal */}
      {showNewGroup && (
        <div className="modal-overlay" onClick={() => { setShowNewGroup(false); resetNewGroupForm(); }}>
          <div className="new-group-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create New Group</h3>
              <button className="icon-button" onClick={() => { setShowNewGroup(false); resetNewGroupForm(); }}>
                <FiX />
              </button>
            </div>
            
            <div className="modal-content">
              <form onSubmit={handleCreateGroupSubmit}>
                <div className="form-group">
                  <label>Group Name *</label>
                  <input
                    type="text"
                    placeholder="Enter group name (e.g., Ward 5 Coordinators)"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    autoFocus
                    className="form-input"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Description (Optional)</label>
                  <textarea
                    placeholder="Enter group description"
                    value={newGroupDescription}
                    onChange={(e) => setNewGroupDescription(e.target.value)}
                    rows="3"
                    className="form-textarea"
                  />
                </div>
                
                {/* Group Type Selection - Radio Buttons */}
                <div className="form-group">
                  <label>Group Type *</label>
                  {availableGroupTypes.length === 0 ? (
                    <div className="no-permission-message">
                      <FiAlertCircle />
                      <span>You do not have permission to create groups</span>
                    </div>
                  ) : (
                    <div className="group-type-selection">
                      {availableGroupTypes.map(type => (
                        <div 
                          key={type.value}
                          className={`group-type-option ${newGroupType === type.value ? 'selected' : ''}`}
                          onClick={() => selectGroupType(type.value)}
                        >
                          <div className="group-type-option-content">
                            <div className="group-type-checkbox">
                              <div className={`custom-checkbox ${newGroupType === type.value ? 'checked' : ''}`}>
                                {newGroupType === type.value && <FiCheck />}
                              </div>
                            </div>
                            <div className="group-type-icon" style={{ backgroundColor: type.color + '20', color: type.color }}>
                              {type.icon}
                            </div>
                            <div className="group-type-info">
                              <div className="group-type-header">
                                <span className="group-type-label" style={{ color: type.color }}>{type.label}</span>
                                {newGroupType === type.value && (
                                  <FiCheck className="option-check" style={{ color: type.color }} />
                                )}
                              </div>
                              <p className="group-type-description">{type.description}</p>
                              {type.details && (
                                <div className="group-type-details">
                                  {type.details.map((detail, idx) => (
                                    <div key={idx} className="group-type-detail">
                                      <span className="detail-bullet">•</span>
                                      <span className="detail-text">{detail}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {!newGroupType && availableGroupTypes.length > 0 && (
                    <p className="form-error">Please select a group type</p>
                  )}
                </div>
                
                <div className="form-group">
                  <div className="form-header">
                    <label>Add Members (Optional)</label>
                    <div className="selected-count">
                      {selectedUsersForGroup.length} selected
                    </div>
                  </div>
                  
                  <div className="search-box-inline">
                    <FiSearch className="search-icon" />
                    <input
                      type="text"
                      placeholder="Search users..."
                      onChange={(e) => loadUsersForGroup(e.target.value)}
                      className="search-input"
                    />
                  </div>
                  
                  {selectedUsersForGroup.length > 0 && (
                    <div className="selected-users-section">
                      <h5>Selected Members ({selectedUsersForGroup.length})</h5>
                      <div className="selected-users-grid">
                        {selectedUsersForGroup.map(user => (
                          <div key={user.id} className="selected-user-card">
                            <div className="selected-user-info">
                              <div className="avatar small">
                                {user.avatar ? (
                                  <img src={user.avatar} alt={user.name} />
                                ) : (
                                  <div className="avatar-placeholder">
                                    {user.name?.charAt(0) || 'U'}
                                  </div>
                                )}
                              </div>
                              <div className="user-details">
                                <span className="user-name">{user.name}</span>
                                <span className="user-email">{user.email}</span>
                              </div>
                            </div>
                            <div className="user-actions">
                              <button 
                                type="button"
                                className={`admin-toggle-btn ${user.is_admin ? 'is-admin' : ''}`}
                                onClick={() => toggleUserAdminStatus(user.id)}
                                title={user.is_admin ? 'Remove admin' : 'Make admin'}
                              >
                                <FiUserCheck />
                                {user.is_admin && <span className="admin-badge">Admin</span>}
                              </button>
                              <button 
                                type="button"
                                className="remove-user-btn"
                                onClick={() => toggleUserForGroup(user)}
                                title="Remove"
                              >
                                <FiX />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="users-list-section">
                    <h5>Available Users ({users.length})</h5>
                    <div className="users-grid">
                      {usersLoading ? (
                        <div className="loading">Loading users...</div>
                      ) : users.length === 0 ? (
                        <div className="no-users">
                          <FiUser size={32} />
                          <p>No users found</p>
                        </div>
                      ) : (
                        users.map(user => (
                          <div 
                            key={user.id} 
                            className={`user-select-card ${selectedUsersForGroup.some(u => u.id === user.id) ? 'selected' : ''}`}
                            onClick={() => toggleUserForGroup(user)}
                          >
                            <div className="user-select-info">
                              <div className="checkbox-wrapper">
                                <div className={`custom-checkbox ${selectedUsersForGroup.some(u => u.id === user.id) ? 'checked' : ''}`}>
                                  {selectedUsersForGroup.some(u => u.id === user.id) && <FiCheck />}
                                </div>
                              </div>
                              <div className="avatar small">
                                {user.avatar ? (
                                  <img src={user.avatar} alt={user.name} />
                                ) : (
                                  <div className="avatar-placeholder">
                                    {user.name?.charAt(0) || 'U'}
                                  </div>
                                )}
                                <div className={`online-indicator ${user.is_online ? 'online' : 'offline'}`}></div>
                              </div>
                              <div className="user-details">
                                <span className="user-name">{user.name}</span>
                                <span className="user-email">{user.email}</span>
                              </div>
                            </div>
                            {selectedUsersForGroup.some(u => u.id === user.id && u.is_admin) && (
                              <div className="admin-indicator">
                                <FiUserCheck /> Admin
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="modal-actions">
                  <button 
                    type="button"
                    className="secondary-button"
                    onClick={() => { setShowNewGroup(false); resetNewGroupForm(); }}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    className="primary-button"
                    disabled={!newGroupName.trim() || !newGroupType || availableGroupTypes.length === 0}
                  >
                    Create Group
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Group Details Modal */}
      <GroupDetailsModal />
    </div>
  );
};

export default Chat;